import React, { useEffect, useMemo } from "react";
import "./../components/Report.scss";
import { _hasPermission } from "../../../../../../shared/helpers/access";
import { _axiosCall } from "../../../../../../shared/helpers/apicalls";
import CircularProgress from "./../../components/CircularProgress";
import CustomLineChart from "./../../components/CustomLineChart";
import CustomSwitch from "./../../components/CustomSwitch";
import Indicator from "./../../components/Indicator";
import Divider from "./../../components/Divider";
import HorizontalBarChart from "./../../components/HorizontalBarChart";
import Footer from "./../components/Footer";
import CoverPage from "./../components/CoverPage";

import {
  _capitalizeText,
  _formatNumber,
  _formatPrice,
  _formatCurrency,
  _nameToUrl,
  _shouldShow,
  _getBuildingURL,

  _replaceAbbreviations,
  _formatBed,

  _isEqual,
  _tranformTagsQuery,
  _isNewUser,
  _isEnterpriseUser,
  _objectEqual,
  getCohortQuery,
  _activityFQ,
  _isHouse,
  _formatBigPrice,
  _activityFL,
} from "../../../../../../shared/helpers/utils";
import { formatDateRange,  generatePeriodNames, percentageChange, prepareResForAI, validateNestedData, filterAIData, getCurrentPeriod, getPriorPeriod, getRangeEnd, getRangeStart, openAIModel, parseEditedHTML , _reportGeoLabel, _reportScopePrompt} from "../../../exportreport/utils";
import CustomSkeleton from "../components/Skeleton";

const apiURL = process.env.API_ROOT;

let statTypes = [
  {
    key: "newdev",
    title: "New Development Condo & Co-op",
    name: "New Development",
    checkedColor: "#FF206E",
    activitesQuery:
      '(event_tags:(event-stage_sponsor) AND -address_2_normalized:"")',
    default: true,
  },
];

const statFacet = {
  "med_price":"percentile(price, 50)",
  "by_borough" : {
    "type" : "terms",
    "field" : "borough",
    "facet" : {
      "med_price":"percentile(price, 50)"
    }
  },
  "ppsf":{
    "type":"query",
    "q":"sq_ft:[300 TO 20000]",
    "facet":{
      "med":"percentile(div(price, sq_ft), 50)"
    }
  }
};

const BlendedLayout = (props) => {
  const {
    fetchInProgress,
    setFetchInProgress,
    user,
    setReportLoaded,
    reportData,
    setReportData,
    searchTags,
    summarizeGeographies,
    customDate,
    submit,
    setSubmit,
    color,
    setAiResponse,
    AiResponse,
    setFetchAi,
    fetchAi,
    setAIInProgress,
    editedContent = ""
  } = props;
  const { dateOptions, theme } = customDate;
  const currentPeriod = getCurrentPeriod(dateOptions)
  const priorPeriod = getPriorPeriod(dateOptions)

  const priorYearPeriod = priorPeriod;
  const rangeEnd = getRangeEnd(dateOptions);
  const rangeStart = getRangeStart;
  const gap = dateOptions.gap;
  const currentPeriodName = generatePeriodNames(dateOptions).currentPeriod;
  const priorYearPeriodName = generatePeriodNames(dateOptions).priorYearPeriod;

  // Build edited content map once
  const editedMap = useMemo(function(){ return parseEditedHTML(editedContent); }, [editedContent]);

  // Render edited HTML if present and marked as changed, otherwise render fallback JSX
  function renderEditedOrDefault(id, fallback) {
    if (!editedMap || !editedMap[id]) return fallback;

    try {
      var parser = new DOMParser();
      var doc = parser.parseFromString(editedMap[id], "text/html");
      var el = doc.body.firstElementChild;

      if (el && el.getAttribute("data-changed") === "true") {
        return <div dangerouslySetInnerHTML={{ __html: el.outerHTML }} />;
      }
    } catch (e) {
      console.warn("Failed to parse edited HTML for id:", id, e);
    }

    return fallback;
  }

  useEffect(() => {
    if (!reportData && !fetchInProgress && submit) {
      handleCombinedAPIRequests();
    }
  }, [submit]);
  useEffect(() => {
    const fetchAIData = async () => {
      setFetchAi && setFetchAi(false);
      setAIInProgress(true);
      let aiData = prepareResForAI(JSON.parse(JSON.stringify(reportData.facets)));
      const aiResponse = await handleAIGenerate(aiData);
      setAiResponse && setAiResponse(aiResponse);
      setAIInProgress(false);
    };
    if (reportData && fetchAi) {
      fetchAIData();
    }
  }, [reportData, fetchAi])

  const dataFacet = {
    contract_signed: {
      type: "query",
      q: "topic:listings AND event_tags:contract-signed AND -event_tags:rent AND -event_tags:*commercial*",
      facet: {
        current_period: {
          type: "query",
          q: `time:${currentPeriod}`,
          facet: statFacet,
        },
        prior_year_period: {
          type: "query",
          q: `time:${priorYearPeriod}`,
          facet: statFacet,
        },
        chart: {
          range: {
            field: "time",
            start: rangeStart,
            end: rangeEnd,
            gap: gap,
            facet: {
              dom: {
                type: "query",
                q: "days_on_market:[3 TO 720]",
                facet: {
                  avg: "avg(days_on_market)",
                  med: "percentile(days_on_market, 50)",
                },
              },
            },
          },
        },
      },
    },
    recorded_sold: {
      type: "query",
      q: "topic:transactions AND event_tags:recorded-sale AND -event_tags:*commercial* AND  -event_tags:property-type_parking-space AND -event_tags:property-type_storage-room AND -event_tags:parcels_bulk-sale",
      facet: {
        current_period: {
          type: "query",
          q: `time:${currentPeriod}`,
          facet: statFacet,
        },
        prior_year_period: {
          type: "query",
          q: `time:${priorYearPeriod}`,
          facet: statFacet,
        },
        chart: {
          range: {
            field: "time",
            start: rangeStart,
            end: rangeEnd,
            gap: gap,
            facet: {
              med_price: "percentile(price, 50)",
              sum_price: "sum(price)",
              ppsf: {
                type: "query",
                q: "sq_ft:[300 TO 20000]",
                facet: {
                  avg: "avg(div(price, sq_ft))",
                  med: "percentile(div(price, sq_ft), 50)",
                },
              },
            },
          },
        },
      },
    },
  };

  const getInventory = async () => {
    let q = "*:*";
    let locQuery = _tranformTagsQuery(searchTags);

    if (locQuery) {
      q = locQuery;
    }
    setFetchInProgress(true);

    let query = {
      "q": q,
      "fq": [
        "deleted:false",
        "{!join from=alternate_keys fromIndex=we3-buildings to=building}(((offering_submitted_date:[2010-11-01T00:00:00Z TO NOW] OR offering_accepted_date:[2010-11-01T00:00:00Z TO NOW] OR offering_verified_status:(active \"coming soon\" \"offering plan required\" \"offering plan acquired\" \"condo declaration vetted\")) AND offering_verified_status:(\"\" \"monitoring\" \"reviewed\" \"offering plan required\" \"offering plan acquired\" \"condo declaration vetted\" \"vetting required\" \"vetted\" \"coming soon\" \"active\" \"sold out\") AND deleted:false AND building_status:(pipeline)) OR ((offering_submitted_date:[2010-11-01T00:00:00Z TO NOW] OR offering_accepted_date:[2010-11-01T00:00:00Z TO NOW] OR offering_verified_status:(active \"coming soon\" \"offering plan required\" \"offering plan acquired\" \"condo declaration vetted\")) AND offering_verified_status:(\"\" \"monitoring\" \"reviewed\" \"offering plan required\" \"offering plan acquired\" \"condo declaration vetted\" \"vetting required\" \"vetted\" \"coming soon\" \"active\" \"sold out\") AND deleted:false AND building_status:(sponsor)) OR ((ownership_type:(*condo*) AND -ownership_type:condop AND property_category:(resi* mixed*use) AND -full_street_address:\"\" AND -full_street_address:/.* garage .*/ AND number_of_buildings:[1 TO *] AND (condo_num:['1' TO *] OR (offering_verified_status:active AND building_status:resale)) AND ((tax_assessed_residential_units:[1 TO *] OR building_class:R3 OR (offering_verified_status:active AND building_status:resale)) AND -building_class:R9) AND -offering_verified_status:\"excluded\" AND deleted:false) AND building_status:resale) OR (ownership_type:(*co?op* *coop* condop*) AND number_of_buildings:[1 TO *] AND -ownership_occupancy_type:rental) OR ((ownership_type:(single?family multi?family) AND total_units:[1 TO 6]) OR (ownership_type:rental AND total_units:[* TO 3])))",
        "-offering_unit_abandoned:true",
        "-offering_verified_status:stalled"
      ],
      "rows": 0,
      "start": 0,
      "wt": "json",
      "json.facet": {
        "unsold": {
          "type": "query",
          "q": "((offering_verified_status:active AND source_organizations:NYAG AND offering_baths:[1 TO *] AND offering_price:* AND -offering_file_number:\"\" AND -address_2_normalized:\"\") OR (current_status:(active \"in contract\")) OR (verification_source:(user-verified-valid CDEC ADEC NYCDOF) AND -address_2_normalized:\"\") OR building_class:R3) AND (current_status:((active) OR (current_status:shadow AND -offering_construction:conversion AND property_status:sponsor AND (offering_submitted_date:[2010-11-01T00:00:00Z TO NOW] OR offering_accepted_date:[2010-11-01T00:00:00Z TO NOW])) OR (current_status:transfer AND -offering_construction:conversion AND property_status:sponsor AND (offering_submitted_date:[2010-11-01T00:00:00Z TO NOW] OR offering_accepted_date:[2010-11-01T00:00:00Z TO NOW])))) AND (property_type:((*residential*condo* condo \"condo unit\" \"resident manager unit\" *coop* *affordable* \"\" *dwelling* *timeshare* *residential* *apartment*))) AND property_status:(\"sponsor\") AND (source_organizations:(NYAG CDEC ADEC) OR verification_source:(user-verified-valid CDEC ADEC NYCDOF) OR current_status:active) AND -address_2_normalized:\"\"",
          "facet": {
            "buildings": "unique(building)",
            "active": {
              "type": "query",
              "q": "current_status:active"
            }
          }
        },
        current_period: {
          type: "query",
          q: `offering_accepted_date:${currentPeriod} AND -address_2_normalized:"" AND baths:[1 TO *]`
        },
        prior_year_period: {
          type: "query",
          q: `offering_accepted_date:${priorYearPeriod} AND -address_2_normalized:"" AND baths:[1 TO *]`
        },
      }
    };
    
    let axiosData = {
      url: apiURL + "/query/we3-properties",
      method: "post",
      query: query,
    };

    return _axiosCall(axiosData)
      .then((res) => {
        if (res && res.facets) {
          setFetchInProgress(false);
        }
        return res;
      })
      .catch((error) => {
        console.log("error: " + error);
      });
  };
  
  const loadData = async () => {
    let q = "*:*";
    let locQuery = _tranformTagsQuery(searchTags);

    if (locQuery) {
      q = locQuery;
    }

    const defaultQ = getCohortQuery(user, true);
    const fq = [
      ..._activityFQ.slice(),
      ...[`{!join from=alternate_keys fromIndex=we3-buildings to=building}${defaultQ}`],
    ];
    setFetchInProgress(true);

    let facet = {};
    statTypes.map((item) => {
      facet[item.key] = {
        "type": "query",
        "q": item.activitesQuery,
        "facet": dataFacet,
      };
    });

    let axiosData = {
      url: apiURL + "/query/we3-activities-3",
      method: "post",
      query: {
        q: q,
        fq: fq,
        wt: "json",
        rows: 0,
        "json.facet": facet,
      },
    };

    return _axiosCall(axiosData)
      .then(async (res) => {
        return res;
      })
      .catch((error) => {
        console.log("error: " + error);
      });
  };
  
  const handleCombinedAPIRequests = async () => {
    try {
      const [inventoryData, res] = await Promise.all([getInventory(), loadData()]);

      let aiData;
      if (res && res.facets) {
        if (inventoryData && inventoryData.facets) {
          res.facets["inventory"] = inventoryData.facets;
        }
        setFetchInProgress(false);
        setReportLoaded(true);
        setSubmit(false);
        setReportData(res);
        setFetchAi(true)
      }
      return res;
    } catch (error) {
      console.error('Error in combined API requests:', error);
      setFetchInProgress(false);
    }
  };

  const handleAIGenerate = async (data) => {
    const prompt = `Given the following data give me an analysis that speaks to current period trends vs prior year periods, the chart contains more history.  The analysis should look at root nodes (newdev) and separately analayze  contracts_signed and recorded_sold.  Ignore contracts_signed by boroughs.  Additionally analyze the inventory.unsold node to summarize current unsold inventory and active (on the market) inventory, ignore inventory.current_period and inventory.prior_year_period.  The analysis should be easy to understand for consumer audiences.  For contracts_signed focus only on the count of contracts and days on market (dom), for recorded_sold ignore count and focus on prices.  The analysis should be concise, 2 sentences.  Just text.  Abbreviate long currency so it's $1.5M, round if less than $1M.  If less than $1M use K.  Number should be in format ###,###,###  The results should be in valid raw JSON format, no formatting.  { "newdev" : { contracts_signed: "analysis in text", recorded_sales: "analysis in text"}, inventory : "analysis in text" }
    
    The report contains the following key stats:${JSON.stringify(filterAIData(data))}
    
    GEOGRAPHY: ${_reportGeoLabel(searchTags, summarizeGeographies)}. ${_reportScopePrompt(searchTags, summarizeGeographies)}
    
    Current Period: ${currentPeriodName}
    Prior Year Period: ${priorYearPeriodName}`;

    const queryData = {
      messages: [
        {
          role: "system",
          content: "You are a NYC real estate expert that provides insightly data-driven analysis that are easy to understand.  You speak in a professional yet friendly manner.  Think TV or news analysts.",
        },
        { role: "user", content: prompt },
      ],
      model: openAIModel,
      response_format: { type: "json_object" },
    };

    const axiosData = {
      url: apiURL + "/ai/chat",
      method: "post",
      query: queryData,
    };
    let aiResponse = {};
    try {
      const res = await _axiosCall(axiosData);
      if (res && res.choices && res.choices[0] && res.choices[0].message && res.choices[0].message.content) {
        const result = res.choices[0].message.content;
        try {
          aiResponse = JSON.parse(result);
        } catch (e) {}
      }
    } catch (error) {
      console.log("Error generating summary:", error);
    }
    return aiResponse;
  };
  
  // Subcomponent
  const PropertyTypePage = (props) => {
    const { type } = props;
    const hasLocation = searchTags && searchTags.length>0;

    // Namespace helper to keep IDs unique per type
    var ns = function(localId){
      var key = type && type.key ? String(type.key) : "unknown";
      return key + "-" + localId;
    };

    return (
      <div className="report-container page-section newdev">
        <section className="report-section">

          {renderEditedOrDefault(
            ns("headingLocation"),
            <h3 className="header-text heading-location editable track-original" id={ns("headingLocation")}>
              {hasLocation ? _capitalizeText(summarizeGeographies(searchTags)) : "New York City"}
            </h3>
          )}

          {renderEditedOrDefault(
            ns("headerSubheading"),
            <div className="header-subtext__heading editable track-original" id={ns("headerSubheading")}>{type.name}</div>
          )}

          <div className="subtext-grid">
            {renderEditedOrDefault(
              ns("headerInnerSubheading"),
              <div className="header-inner-subtext__heading editable track-original" id={ns("headerInnerSubheading")}>
                {type.title}
              </div>
            )}
            <div>
              <div>Date Range</div>
              {renderEditedOrDefault(
                ns("dateRange"),
                <div className="editable track-original" id={ns("dateRange")}>{priorYearPeriodName} vs. {currentPeriodName}</div>
              )}
            </div>
          </div>

          <div className="semi-progress-container">
            <div className="inner_heading_container">
              <div>Contracts</div>
              <CircularProgress value={(validateNestedData(
                reportData,
                `facets.${type.key}.contract_signed.current_period.count`
              ) &&
              validateNestedData(
                reportData,
                `facets.${type.key}.contract_signed.prior_year_period.count`
              )
                ? percentageChange(
                    reportData.facets[type.key].contract_signed.current_period.count,
                    reportData.facets[type.key].contract_signed.prior_year_period.count
                  )
                : "0")} />
            </div>

            <CustomSwitch
              rows={[
                {
                  firstValue: "Volume",
                  middleValue: "",
                  lastValue: "Units Added",
                  header: true,
                },
                {
                  firstValue: validateNestedData(reportData, `facets.${type.key}.contract_signed.current_period.count`)  ? _formatNumber(reportData.facets[type.key].contract_signed.current_period.count) : "--",
                  middleValue: currentPeriodName,
                  lastValue: validateNestedData(reportData, `facets.inventory.current_period.count`)  ? _formatNumber(reportData.facets.inventory.current_period.count) : "--",
                },
                {
                  firstValue: validateNestedData(reportData, `facets.${type.key}.contract_signed.prior_year_period.count`)  ? _formatNumber(reportData.facets[type.key].contract_signed.prior_year_period.count) : "--",
                  middleValue: priorYearPeriodName,
                  lastValue: validateNestedData(reportData, `facets.inventory.prior_year_period.count`)  ? _formatNumber(reportData.facets.inventory.prior_year_period.count) : "--",
                },
              ]}
              hightlightedElement={1}
            />

            <div className="inner_heading_container">
              <div>INVENTORY</div>
              <div className="inner_stat">
                <hr/>
                <div className="stat">
                  {validateNestedData(reportData, `facets.inventory.unsold.count`)  ? _formatNumber(reportData.facets.inventory.unsold.count) : "--"}
                </div>
                <p className="stat-desc">Total New Units</p>
                <hr/>
              </div>
            </div>
          </div>

          <div className="report-layout-outer-xl-switch-container">
            <div className="content subtext">
              {renderEditedOrDefault(
                ns("contractsSignedNarrative"),
                <p className="editable track-original" id={ns("contractsSignedNarrative")}>
                  { AiResponse && AiResponse[type.key] && AiResponse[type.key].contracts_signed ? AiResponse[type.key].contracts_signed : <CustomSkeleton count={6}/> }
                </p>
              )}
              {renderEditedOrDefault(
                ns("inventoryNarrative"),
                <p className="editable track-original" id={ns("inventoryNarrative")}>
                  { AiResponse && AiResponse.inventory ? AiResponse.inventory : <CustomSkeleton count={8}/> }
                </p>
              )}
            </div>

            <div className="switch-xl-container">
              <CustomSwitch
                rows={[
                  {
                    firstValue: "On the market",
                    middleValue: "",
                    lastValue: "Shadow",
                    header: true,
                  },
                  { firstValue: validateNestedData(reportData, `facets.inventory.unsold.active.count`)  ? _formatNumber(reportData.facets.inventory.unsold.active.count) : "--", middleValue: validateNestedData(reportData, `facets.inventory.unsold.active.count`)  ? _formatNumber((reportData.facets.inventory.unsold.count - reportData.facets.inventory.unsold.active.count)) + " Units" : "--" },
                ]}
                hightlightedElement={0}
              />
              <div>
                <span>{validateNestedData(reportData, `facets.inventory.unsold.active.count`)  ? Math.round(((reportData.facets.inventory.unsold.count - reportData.facets.inventory.unsold.active.count) / reportData.facets.inventory.unsold.count) * 100) + "%" : "--"}</span> of new inventory is unlisted
              </div>
            </div>
          </div>
        </section>

        <Divider theme={theme} />

        <section className="report-section">
          <div className="custom-charts horizontal-chart-section">
            <div className="indicator-container">
              <h3 className="heading">CLOSINGS</h3>
              <Indicator
                price={validateNestedData(reportData, `facets.${type.key}.recorded_sold.current_period.med_price`)  ? _formatPrice(reportData.facets[type.key].recorded_sold.current_period.med_price) : "--"}
                percentage={validateNestedData(
                    reportData,
                    `facets.${type.key}.recorded_sold.current_period.med_price`
                  )
                    ? percentageChange(
                        reportData.facets[type.key].recorded_sold.current_period.med_price,
                        reportData.facets[type.key].recorded_sold.prior_year_period.med_price
                      )
                    : "--"}
                reverse={true}
                name="Med Price"
              />
              <Indicator
                price={validateNestedData(reportData, `facets.${type.key}.recorded_sold.current_period.ppsf.med`)  ? "$" + _formatNumber(reportData.facets[type.key].recorded_sold.current_period.ppsf.med) : "--"}
                percentage={validateNestedData(
                    reportData,
                    `facets.${type.key}.recorded_sold.current_period.ppsf.med`
                  )
                    ? percentageChange(
                        reportData.facets[type.key].recorded_sold.current_period.ppsf.med,
                        reportData.facets[type.key].recorded_sold.prior_year_period.ppsf.med
                      )
                    : "--"}
                reverse={true}
                name="Price S/F"
              />
            </div>

            <div className="chart_wrapper">
              {renderEditedOrDefault(
                ns("recordedSalesSubtext"),
                <div className="subtext editable track-original" id={ns("recordedSalesSubtext")}>
                  {AiResponse && AiResponse[type.key] && AiResponse[type.key].recorded_sales ? AiResponse[type.key].recorded_sales : <CustomSkeleton count={4}/>}
                </div>
              )}
              <div className="chart_inner">
                {(!hasLocation) ?
                  <div className="Chart-1">
                    <div className="heading"> MEDIAN PRICE</div>
                    <HorizontalBarChart
                      theme={theme}
                      data={[
                        { name: "MN", value: validateNestedData(reportData, `facets.${type.key}.recorded_sold.current_period.by_borough.buckets`) && reportData.facets[type.key].recorded_sold.current_period.by_borough.buckets.filter(function(item){ return item.val == "manhattan"; })[0]  ? (reportData.facets[type.key].recorded_sold.current_period.by_borough.buckets.filter(function(item){ return item.val == "manhattan"; })[0].med_price) : 0, barDisplayInnerValue: validateNestedData(reportData, `facets.${type.key}.recorded_sold.current_period.by_borough.buckets`) && reportData.facets[type.key].recorded_sold.current_period.by_borough.buckets.filter(function(item){ return item.val == "manhattan"; })[0]  ? _formatPrice(reportData.facets[type.key].recorded_sold.current_period.by_borough.buckets.filter(function(item){ return item.val == "manhattan"; })[0].med_price) : 0 },
                        { name: "BK", value: validateNestedData(reportData, `facets.${type.key}.recorded_sold.current_period.by_borough.buckets`) && reportData.facets[type.key].recorded_sold.current_period.by_borough.buckets.filter(function(item){ return item.val == "brooklyn"; })[0]  ? (reportData.facets[type.key].recorded_sold.current_period.by_borough.buckets.filter(function(item){ return item.val == "brooklyn"; })[0].med_price) : 0, barDisplayInnerValue: validateNestedData(reportData, `facets.${type.key}.recorded_sold.current_period.by_borough.buckets`) && reportData.facets[type.key].recorded_sold.current_period.by_borough.buckets.filter(function(item){ return item.val == "brooklyn"; })[0]  ? _formatPrice(reportData.facets[type.key].recorded_sold.current_period.by_borough.buckets.filter(function(item){ return item.val == "brooklyn"; })[0].med_price) : 0},
                        { name: "QN", value: validateNestedData(reportData, `facets.${type.key}.recorded_sold.current_period.by_borough.buckets`) && reportData.facets[type.key].recorded_sold.current_period.by_borough.buckets.filter(function(item){ return item.val == "queens"; })[0]  ? (reportData.facets[type.key].recorded_sold.current_period.by_borough.buckets.filter(function(item){ return item.val == "queens"; })[0].med_price) : 0, barDisplayInnerValue: validateNestedData(reportData, `facets.${type.key}.recorded_sold.current_period.by_borough.buckets`) && reportData.facets[type.key].recorded_sold.current_period.by_borough.buckets.filter(function(item){ return item.val == "queens"; })[0]  ? _formatPrice(reportData.facets[type.key].recorded_sold.current_period.by_borough.buckets.filter(function(item){ return item.val == "queens"; })[0].med_price) : 0 },
                      ]}
                    />
                  </div>
                : null}
                <div className={`Chart-2 ${hasLocation ? "Has-Loc" : ""}`}>
                  <div className="heading">MEDIAN PRICE / SF</div>
                  <CustomLineChart
                    theme={theme}
                    data={reportData.facets[type.key].recorded_sold.chart.buckets.slice(reportData.facets[type.key].recorded_sold.chart.buckets.length - 12,reportData.facets[type.key].recorded_sold.chart.buckets.length).map(function(item){ return {year : formatDateRange(null, null, item.val), price: item.ppsf && item.ppsf.med ? Math.round(item.ppsf.med) : 0}; })}
                    isWeekly={dateOptions.gap}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    )
  }

  if (!reportData) return null;

  return (
    <>
      <CoverPage
        {...props}
        editedContent={editedContent}
        summarizeGeographies={summarizeGeographies} 
        priorYearPeriodName={priorYearPeriodName} 
        currentPeriodName={currentPeriodName}
      />
      {statTypes.map(function(type){
        return <PropertyTypePage key={type.key} type={type} />
      })}
    </>
  )
};

export default BlendedLayout;
